import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useWorkspaceStore } from '@/stores/workspace.store';

export type WorkspaceHomeMode = 'chat' | 'workbench';

export function useWorkspaceHomeNavigation(workspaceId: () => string) {
  const router = useRouter();
  const auth = useAuthStore();
  const workspace = useWorkspaceStore();
  const mode = computed<WorkspaceHomeMode>(() => auth.user?.workspaceHomeMode ?? 'chat');
  const collapsed = computed(() => auth.user?.workspaceSidebarCollapsed === true);

  async function saveAccount(input: Partial<{
    workspaceHomeMode: WorkspaceHomeMode;
    workspaceSidebarCollapsed: boolean;
  }>): Promise<void> {
    await auth.updateProfile(input);
  }

  async function setCollapsed(value: boolean): Promise<void> {
    if (value === collapsed.value) return;
    await saveAccount({ workspaceSidebarCollapsed: value });
  }

  async function openChat(sessionId = workspace.currentMember?.preferences.lastMasterSessionId ?? null): Promise<void> {
    const id = workspaceId();
    if (!id) { await router.push({ name: 'workspace-management' }); return; }
    await saveAccount({ workspaceHomeMode: 'chat' });
    await router.push({
      name: 'workspace-chat',
      params: { workspaceId: id },
      query: sessionId ? { sessionId } : undefined,
    });
  }

  async function openWorkbench(): Promise<void> {
    const id = workspaceId();
    if (!id) { await router.push({ name: 'workspace-management' }); return; }
    await saveAccount({ workspaceHomeMode: 'workbench' });
    await router.push({ name: 'workspace-dashboard', params: { workspaceId: id } });
  }

  async function rememberSession(sessionId: string | null): Promise<void> {
    const id = workspaceId();
    if (!id || workspace.currentMember?.preferences.lastMasterSessionId === sessionId) return;
    await workspace.updateMyPreferences(id, { lastMasterSessionId: sessionId });
  }

  async function rememberPage(pageId: string | null): Promise<void> {
    const id = workspaceId();
    if (!id || workspace.currentMember?.preferences.lastPageId === pageId) return;
    await workspace.updateMyPreferences(id, { lastPageId: pageId });
  }

  return { mode, collapsed, setCollapsed, openChat, openWorkbench, rememberSession, rememberPage };
}
